// https://ria.pacificlife.com/enroll-ui/[route_here]
// https://annuities.pacificlife.com/enroll-ui/[route_here]
import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { QuestionCircle } from "react-bootstrap-icons";
import ReactTooltip from "react-tooltip";
import Toast from "react-bootstrap/Toast";
import "react-datepicker/dist/react-datepicker.css";
import PropTypes from "prop-types";
import axios from "axios";
import {
    INTERNAL, // ria
    WHOLESALER, // annuities
    TPIA, // ria
    RIA_FIRM, // ria
    VERIFY_FIRM_ID_FOR_ROLES,
    VERIFY_ADVISOR_CRD_FOR_ROLES,
    REQUIRED_FIELDS,
    ANNUITIES_FP_LOGIN_PAGE_URL_PATH,
    RIA_LOGIN_PAGE_URL_PATH,
    REGISTRATION_SUCCESSFUL_URL_PATH
} from "./constants";
import {
    emailValidation,
    userNameValidation,
    emailVerifyMatch
} from "./validation";
import { analytics } from "./analytics";
import { Header, Footer, Disclaimer } from "./components";

const DefaultUserState = {
    firstName: "",
    lastName: "",
    email: "",
    verifyEmail: "",
    firmID: "",
    crd: "",
    userName: ""
};

const DefaultErrorState = {
    firmID: "",
    crd: "",
    email: "",
    verifyEmail: "",
    userName: ""
};

/** Shared Registration Component for TPIA, INTERNAL, WHOLESALER, RIA_FIRM user roles */
const Registration = ({ userRole }) => {
    const [user, setUser] = useState(DefaultUserState);
    const [errors, setErrors] = useState(DefaultErrorState);
    const [toastMessage, setToastMessage] = useState("");
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        analytics();
    }, []);

    useEffect(() => {
        // check if all fields are filled out
        const requiredFields = REQUIRED_FIELDS[userRole];
        for (const element of requiredFields) {
            const fieldName = element;
            if (!user[fieldName]) {
                setSubmitDisabled(true);
                return;
            }
        }
        // check if there is any error
        const errorFields = Object.keys(errors);
        for (const element of errorFields) {
            const fieldName = element;
            if (errors[fieldName] !== "") {
                setSubmitDisabled(true);
                return;
            }
        }
        setSubmitDisabled(false);
    }, [user, errors]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUser((prev) => ({ ...prev, [name]: value }));
    };

    // eslint-disable-next-line no-unused-vars
    const finraCheck = async (type) => {
        if (user.activeContract) {
            return;
        }
        let params = `crdNumber=${user.crd}`;
        if (type === "firmID") {
            params = `firmId=${user.firmID}`;
        }
        const url = `/enroll/ria/user/finradetails/get?${params}`;
        try {
            await axios.get(url);
        } catch (error) {
            // need to know the error response schema
            // to set an appropriate error message
            setToastMessage(`${type} is not valid.`);
        }
    };

    const handleBlur = async (e) => {
        const { name, value } = e.target;
        let errorMessage = "";
        if (name === "firmID") {
            errorMessage =
                value.length >= 2 ? "" : "Firm ID must be at least 2 digits";
        }
        if (name === "crd") {
            errorMessage =
                value.length >= 4
                    ? ""
                    : "Advisor CRD must be at least 4 digits";
            setErrors((prev) => ({ ...prev, crdErr: errorMessage }));
        }

        if (name === "email") {
            const emailValid = emailValidation(value);
            errorMessage = emailValid ? "" : "Enter valid email";
        }

        if (name === "verifyEmail") {
            errorMessage = emailVerifyMatch(user.email, value)
                ? ""
                : "Email addresses do not match";
        }

        if (name === "userName") {
            const valid = userNameValidation(value);
            const tooShortOrLong = value.length < 6 || value.length > 8;
            const hasSpecialCharOrWhiteSpace = /\W|\D/g.test(value);
            if (valid) {
                errorMessage = "";
            } else if (tooShortOrLong) {
                errorMessage =
                    "Username must be between 6 and 8 characters long";
            } else if (hasSpecialCharOrWhiteSpace) {
                errorMessage =
                    "Username must not contain spaces or special charaters";
            }
        }

        setErrors((prev) => ({ ...prev, [name]: errorMessage }));
    };

    const handleCancel = () => {
        setUser(DefaultUserState);
        setErrors(DefaultErrorState);
        if (userRole === WHOLESALER) {
            document.location.href = ANNUITIES_FP_LOGIN_PAGE_URL_PATH;
        } else {
            document.location.href = RIA_LOGIN_PAGE_URL_PATH;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            userRole,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            id: user.userName
        };

        if (VERIFY_FIRM_ID_FOR_ROLES.has(userRole)) {
            if (userRole === RIA_FIRM) {
                payload.firmParent = user.firmID;
            } else {
                payload.firmId = [user.firmID];
            }
        }
        setSubmitting(true);
        try {
            await axios({
                method: "POST",
                url: "/enroll/ria/user/register",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json"
                },
                data: JSON.stringify(payload)
            });
            document.location.href = REGISTRATION_SUCCESSFUL_URL_PATH;
        } catch (error) {
            setToastMessage(
                error.response?.data?.errorMessage ||
                    "Something went wrong. Please try again later."
            );
        }
        setSubmitting(false);
    };

    const closeToast = () => {
        setToastMessage("");
    };

    return (
        <>
            {toastMessage !== "" && (
                <Toast onClose={closeToast} className="toast-cont">
                    <Toast.Header>
                        <strong className="me-auto">{toastMessage}</strong>
                    </Toast.Header>
                </Toast>
            )}
            <Header />
            <div className="mainMyAccount">
                <h1 className="heading" style={{ marginBottom: "2%" }}>
                    Create an Account
                </h1>
                <div className="subHeading">
                    Register to view supporting product information, manage
                    individual client contracts, and get access to support.
                </div>
                <Row>
                    <Col sm={6}>
                        <label className="sub-title">
                            Personal Information
                        </label>
                    </Col>
                </Row>
                <Form>
                    <Row style={{ marginBottom: "5%" }}>
                        <Col md={6}>
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                                name="firstName"
                                value={user.firstName}
                                maxLength={25}
                                onChange={handleChange}
                            />
                        </Col>
                        <Col md={6}>
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                                name="lastName"
                                value={user.lastName}
                                maxLength={35}
                                onChange={handleChange}
                            />
                        </Col>
                    </Row>
                    <Row style={{ marginBottom: "5%" }}>
                        <Col md={6}>
                            <ReactTooltip multiline />
                            <Form.Label className="emailClass">
                                Email Address
                                <QuestionCircle
                                    className="iconClass"
                                    data-tip={
                                        userRole === INTERNAL ||
                                        userRole === WHOLESALER
                                            ? "Please note that you must use your Pacific Life email address to create your account."
                                            : "Add your full email address associated with your account."
                                    }
                                    tabIndex="-1"
                                />
                            </Form.Label>
                            <Form.Control
                                name="email"
                                value={user.email}
                                maxLength={60}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.email}
                            </span>
                        </Col>
                        <Col md={6}>
                            <Form.Label>Verify Email Address</Form.Label>
                            <Form.Control
                                name="verifyEmail"
                                value={user.verifyEmail}
                                maxLength={60}
                                onChange={handleChange}
                                onPaste={(e) => e.preventDefault()}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.verifyEmail}
                            </span>
                        </Col>
                    </Row>
                    {(VERIFY_FIRM_ID_FOR_ROLES.has(userRole) ||
                        VERIFY_ADVISOR_CRD_FOR_ROLES.has(userRole)) && (
                        <div
                            style={{
                                marginBottom: "5%"
                            }}
                        >
                            <Row>
                                <Col sm={6}>
                                    <label className="sub-title">
                                        Identity Verification
                                    </label>
                                </Col>
                            </Row>
                            <Row style={{ marginBottom: "5%" }}>
                                {VERIFY_FIRM_ID_FOR_ROLES.has(userRole) && (
                                    <Col md={6}>
                                        <ReactTooltip multiline />
                                        <Form.Label className="firmIdStyle">
                                            Firm ID/CRD
                                            <QuestionCircle
                                                className="iconClass"
                                                data-tip="Enter the firm identification number or FINRA-issued CRD (Central Registration Depository) number used with Pacific Life"
                                                tabIndex="-1"
                                            />
                                        </Form.Label>
                                        <Form.Control
                                            name="firmID"
                                            value={user.firmID}
                                            onChange={handleChange}
                                            placeholder="12345"
                                            maxLength="8"
                                            onBlur={handleBlur}
                                        />
                                        <span className="validation-error-message">
                                            {errors.firmID}
                                        </span>
                                    </Col>
                                )}
                                <Col />
                            </Row>
                        </div>
                    )}
                    <Row>
                        <Col md={6}>
                            <label className="sub-title">
                                Login Information
                            </label>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <ReactTooltip multiline id="username-tooltip" />
                            <Form.Label className="createUsername">
                                User Name
                                <QuestionCircle
                                    className="iconClass"
                                    data-tip="Remember this! You will need it along with your password to access your account information"
                                    tabIndex="-1"
                                />
                            </Form.Label>
                            <Form.Control
                                name="userName"
                                maxLength={8}
                                value={user.userName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            <span className="validation-error-message">
                                {errors.userName}
                            </span>
                        </Col>
                        <Col md={6}>
                            <div className="sublabel">
                                Must be between 6 and 8 characters
                                <br />
                                May contain letters and numbers
                                <br />
                                No spaces or special characters
                            </div>
                        </Col>
                    </Row>
                    {(VERIFY_FIRM_ID_FOR_ROLES.has(userRole) ||
                        VERIFY_ADVISOR_CRD_FOR_ROLES.has(userRole)) && (
                        <div id="regAccessFormMsg">
                            Please note that the information submitted must
                            match the{" "}
                            <a
                                href="/content/dam/paclife/rsd/annuities/public/ria/32069.pdf"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Advisory Onboarding Agreement
                            </a>
                        </div>
                    )}
                    <div className="buttons-myaccount">
                        <Button
                            variant="secondary"
                            onClick={handleCancel}
                            onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                }
                            }}
                            data-track="analytics"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            onClick={handleSubmit}
                            data-track="analytics"
                            disabled={submitDisabled || submitting}
                        >
                            {submitting ? "Registering" : "Register"}
                        </Button>
                    </div>
                </Form>
                <div id="login" className="link-underline">
                    <a href={RIA_LOGIN_PAGE_URL_PATH}>
                        Already have an account?
                    </a>
                </div>
                <Disclaimer />
            </div>
            <Footer />
        </>
    );
};

export default Registration;

Registration.propTypes = {
    userRole: PropTypes.oneOf([TPIA, INTERNAL, RIA_FIRM, WHOLESALER])
};
